import { queryOptions, useQuery } from '@tanstack/react-query';
import { isNotNil } from 'rambda';
import { generateListResponseScheme, type ListRespondedWith } from '@/models/response';
import { type Tag, tagScheme } from '@/models/user/tag';
import { fetcher } from '@/utils/fetcher';

export async function getTags(signal?: AbortSignal): Promise<Tag[]> {
  const response = await fetcher<ListRespondedWith<Tag>>('/GetTags', undefined, {
    signal,
    schema: generateListResponseScheme(tagScheme),
    validResponseCodes: [0, 1],
  });

  return response?.Data ?? [];
}

export async function getGroupedTagsByMember(signal?: AbortSignal): Promise<Record<number, Tag[]>> {
  const tags = await getTags(signal);

  const result: Record<number, Tag[]> = {};
  for (const tag of tags) {
    for (const member of tag.TagMember) {
      const memberId = member.Id;
      if (!result[memberId]) result[memberId] = [];
      result[memberId].push(tag);
    }
  }

  return result;
}

export const tagListQueryOptions = queryOptions({
  queryKey: ['/GetTags'],
  queryFn: ({ signal }) => getTags(signal),
  placeholderData: [] as Tag[],
});

export default function useTags() {
  const query = useQuery(tagListQueryOptions);

  return {
    tags: query.data ?? [],
    ...query,
  };
}

export function useTagsByUserId(userId: Nullable<number>) {
  const query = useQuery({
    ...tagListQueryOptions,
    enabled: isNotNil(userId),
    select: (data) => data.filter((tag) => tag.TagMember.some((member) => member.Id === userId)),
  });

  return {
    tags: query.data ?? [],
    ...query,
  };
}

export function useTagById(tagId: Nullable<number>) {
  return useQuery({
    ...tagListQueryOptions,
    enabled: isNotNil(tagId),
    select: (data) => data.find((t) => t.Id === tagId),
  });
}
